import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { staffApi } from "@client/api/staff";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";

export const Route = createFileRoute("/_authenticated/results/$attemptId")({
  component: ResultDetail,
});

function ResultDetail() {
  const { attemptId } = Route.useParams();

  const { data } = useQuery({
    queryKey: ["result", attemptId],
    queryFn: () => staffApi.result(attemptId),
  });

  const qById = new Map((data?.questions ?? []).map((q) => [q.id, q]));
  const aByIndex = new Map(
    (data?.answers ?? []).map((a) => [a.question_index, a]),
  );

  return (
    <StaffShell
      title={data?.attempt?.full_name ?? "Attempt"}
      subtitle={
        (data?.attempt?.exam_sessions as { title: string } | null)?.title
      }
      showBack
    >
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="panel space-y-3 p-5">
          <h2 className="hall-heading text-lg font-bold">Summary</h2>
          {data?.result ? (
            <>
              <p className="hall-heading text-5xl font-bold">
                {Number(data.result.percentage).toFixed(1)}%
              </p>
              <Badge variant={data.result.passed ? "default" : "destructive"}>
                {data.result.passed ? "PASS" : "FAIL"}
              </Badge>
              <dl className="space-y-1 pt-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Correct</dt>
                  <dd>{data.result.correct_count}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Wrong</dt>
                  <dd>{data.result.wrong_count}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Unanswered</dt>
                  <dd>{data.result.unanswered_count}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Passing score</dt>
                  <dd>{data.result.passing_score}%</dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Not graded yet.</p>
          )}
          <dl className="space-y-1 border-t border-border pt-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Version</dt>
              <dd>
                {
                  (data?.attempt?.exam_versions as { label: string } | null)
                    ?.label
                }
              </dd>
            </div>

            <div className="col-span-2 pt-2 pb-1 border-b border-border font-medium text-foreground">
              Candidate
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Name</dt>
              <dd>{data?.attempt?.full_name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Examination No.</dt>
              <dd>{data?.attempt?.examination_number}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Country</dt>
              <dd>{data?.attempt?.country}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Language</dt>
              <dd>{data?.attempt?.language}</dd>
            </div>

            <div className="col-span-2 pt-2 pb-1 border-b border-border font-medium text-foreground mt-2">
              Exam Location (IP-based)
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">City</dt>
              <dd>{data?.attempt?.geo_city ?? "N/A"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Region</dt>
              <dd>{data?.attempt?.geo_region ?? "N/A"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Country</dt>
              <dd>{data?.attempt?.geo_country ?? "N/A"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">IP Address</dt>
              <dd>{data?.attempt?.ip_address ?? "N/A"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Captured At</dt>
              <dd>
                {data?.attempt?.geo_captured_at
                  ? new Date(data.attempt.geo_captured_at).toLocaleString()
                  : "N/A"}
              </dd>
            </div>
          </dl>
        </div>

        <div className="space-y-6">
          <div className="panel p-5">
            <h2 className="hall-heading text-lg font-bold">Answer sheet</h2>
            <div className="mt-4 space-y-2">
              {(data?.order ?? []).map((qid, i) => {
                const q = qById.get(qid);
                const a = aByIndex.get(i);
                const given = a?.answer;
                const isCorrect =
                  given != null && q && given === q.correct_answer;
                return (
                  <div
                    key={qid}
                    className="rounded-md border border-border p-3 text-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 flex-1">
                        <span className="mr-2 text-muted-foreground">
                          Q{i + 1}.
                        </span>
                        {q?.question_text}
                      </p>
                      <Badge
                        variant={
                          given == null
                            ? "secondary"
                            : isCorrect
                              ? "default"
                              : "destructive"
                        }
                      >
                        {given == null ? "NO ANSWER" : given ? "TRUE" : "FALSE"}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Correct answer: {q?.correct_answer ? "TRUE" : "FALSE"}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel p-5">
            <h2 className="hall-heading text-lg font-bold">
              Activity timeline
            </h2>
            <ul className="mt-4 space-y-2 text-sm">
              {(data?.events ?? []).map((e) => (
                <li
                  key={e.id}
                  className="flex justify-between border-b border-border pb-1"
                >
                  <span>{e.type}</span>
                  <span className="text-muted-foreground">
                    {new Date(e.created_at).toLocaleTimeString()}
                  </span>
                </li>
              ))}
              {!data?.events.length ? (
                <li className="text-muted-foreground">No activity recorded.</li>
              ) : null}
            </ul>
          </div>
        </div>
      </div>
    </StaffShell>
  );
}
